import { BookingModel, BusinessConfig, BusinessTerminology, BusinessUiFlags } from '../types';

type ResolvedUiFlags = Required<BusinessUiFlags>;
type ResolvedTerminology = Required<BusinessTerminology>;

export type ResolvedBookingUxConfig = {
  bookingModel: BookingModel;
  uiFlags: ResolvedUiFlags;
  terminology: ResolvedTerminology;
};

const DEFAULT_UI_FLAGS: ResolvedUiFlags = {
  showHomepageTeamSection: true,
  showHomepageReviewsSection: true,
  showHomepageGallerySection: true,
  showHomepageAboutSection: true,
  showEmployeeBio: true,
  showEmployeeSocials: true,
  showEmployeeProfilePhoto: true,
  showEmployeePickerInBooking: true,
};

const DEFAULT_TERMINOLOGY: ResolvedTerminology = {
  resourceSingular: 'Employee',
  resourcePlural: 'Employees',
  anyAvailableLabel: 'Any Available',
  selectResourceLabel: 'Select employee',
};

export const resolveBookingUxConfig = (config?: BusinessConfig | null): ResolvedBookingUxConfig => {
  const preferences = config?.preferences;
  const terminology = preferences?.terminology;
  const employeeLimit = config?.features?.employeeCount ?? 0;
  const loginlessEmployeeLimit = config?.features?.loginlessEmployeeCount ?? employeeLimit;
  const inferredFrontDeskMode = preferences?.orchestratorMode === true ||
    loginlessEmployeeLimit > employeeLimit;

  const resolveTerm = (
    value: string | undefined,
    fallback: string
  ): string => (typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback);

  return {
    bookingModel: preferences?.bookingModel ?? (inferredFrontDeskMode ? 'resource' : 'staff'),
    uiFlags: {
      ...DEFAULT_UI_FLAGS,
      ...(preferences?.uiFlags ?? {}),
      showEmployeeBio: preferences?.uiFlags?.showEmployeeBio ?? true,
      showEmployeeSocials: preferences?.uiFlags?.showEmployeeSocials ?? true,
      showEmployeeProfilePhoto: preferences?.uiFlags?.showEmployeeProfilePhoto ?? true,
    },
    terminology: {
      resourceSingular: resolveTerm(
        terminology?.resourceSingular,
        inferredFrontDeskMode ? 'Resource' : DEFAULT_TERMINOLOGY.resourceSingular
      ),
      resourcePlural: resolveTerm(
        terminology?.resourcePlural,
        inferredFrontDeskMode ? 'Resources' : DEFAULT_TERMINOLOGY.resourcePlural
      ),
      anyAvailableLabel: resolveTerm(terminology?.anyAvailableLabel, DEFAULT_TERMINOLOGY.anyAvailableLabel),
      selectResourceLabel: resolveTerm(
        terminology?.selectResourceLabel,
        inferredFrontDeskMode ? 'Select a resource' : DEFAULT_TERMINOLOGY.selectResourceLabel
      ),
    },
  };
};

export const resolveAdminBookingUxConfig = (config?: BusinessConfig | null): ResolvedBookingUxConfig => {
  const resolved = resolveBookingUxConfig(config);
  if (resolved.bookingModel === 'resource') return resolved;
  return {
    ...resolved,
    terminology: {
      ...resolved.terminology,
      resourceSingular: 'Employee',
      resourcePlural: 'Employees',
      selectResourceLabel: 'Select employee',
    },
  };
};
